<template>
  <div>
    <v-card>
      <div class="d-flex align-center justify-space-between">
        <v-card-title>Tracked Jobs Report</v-card-title>
      </div>
      <v-container>
        <v-form>
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="fromDate"
                required
                label="From Date"
                type="date"
                :error-messages="fromDateErrors"
                @input="fromDateOnInputOrBlur"
                @blur="fromDateOnInputOrBlur"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="toDate"
                required
                label="To Date"
                type="date"
                :error-messages="toDateErrors"
                @input="toDateOnInputOrBlur"
                @blur="toDateOnInputOrBlur"
              />
            </v-col>
          </v-row>

          <v-btn
            v-if="hasPermission('view system-settings::tracked-jobs')"
            class="mt-5 mb-5 btn-bg-deep-blue"
            :loading="reportGenerating"
            @click="generateReport()"
          >
            Generate Report
          </v-btn>
          <disabled-for-lack-of-role-btn
            v-else
            text="Generate Report"
            permission="view system-settings::tracked-jobs"
            :x-small="false"
            button-class="mb-2"
          />
        </v-form>
      </v-container>
    </v-card>

    <template v-if="reportLoaded">
      <v-data-table
        dense
        :headers="headers"
        :items="items"
        class="elevation-1 mt-2"
      >
        <template #item.class="{ item }">
          {{ item.class.split('\\').pop().split('/').pop() }}
        </template>

        <template #item.arguments="{ item }">
          <v-btn
            tile
            color="primary"
            small
            text
            @click="showJsonModal('JSON', item.arguments)"
          >
            <v-icon
              dark
              left
              class="mr-1"
            >
              mdi-eye
            </v-icon>
            SHOW
          </v-btn>
        </template>

        <template #item.status="{ item }">
          <v-chip
            class="ma-2"
            :color="getStatusColor(item.status)"
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>
      </v-data-table>
    </template>
    <JsonModal
      v-model="jsonModal"
      :title="jsonTitle"
      :json="json"
      @close-modal="closeJsonModal()"
      @click:outside="closeJsonModal()"
    />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { DateTime } from 'luxon';
import JsonModal from '@/components/JsonModal.vue';
import DisabledForLackOfRoleBtn from '../../DisabledForLackOfRoleBtn.vue';

const lessThanToDate = (value, vm) => (value <= vm.toDate);
const greaterThanFromDate = (value, vm) => (value >= vm.fromDate);

export default {
  components: {
    JsonModal,
    DisabledForLackOfRoleBtn,
  },
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      formProcessing: false,
      fromDate: DateTime.local().toISODate(),
      toDate: DateTime.local().toISODate(),
      reportLoaded: false,
      reportGenerating: false,
      json: {},
      jsonTitle: '',
      jsonModal: false,
      headers: [
        { title: 'Class', value: 'class' },
        { title: 'Arguments', value: 'arguments' },
        { title: 'Status', value: 'status' },
        { title: 'Duration', value: 'duration' },
        { title: 'Created', value: 'created_at' },
        { title: 'Updated', value: 'updated_at' },
      ],
      items: [],
    };
  },
  computed: {
    fromDateErrors() {
      const errors = [];

      if (!this.v$.fromDate.$dirty) {
        return errors;
      }
      if (this.v$.fromDate.required.$invalid) {
        errors.push('From Date is required.');
      }

      return errors;
    },
    toDateErrors() {
      const errors = [];

      if (!this.v$.toDate.$dirty) {
        return errors;
      }
      if (this.v$.toDate.required.$invalid) {
        errors.push('To Date is required.');
      }

      return errors;
    },
  },
  created() {
    const routeParams = this.$route.params;
    let generateReport = false;

    if (routeParams.fromDate) {
      this.fromDate = routeParams.fromDate;
      generateReport = true;
    }

    if (routeParams.toDate) {
      this.toDate = routeParams.toDate;
      generateReport = true;
    }

    if (generateReport) {
      this.generateReport();
    }
  },
  methods: {
    generateReport() {
      const vm = this;

      this.v$.$touch();

      if (!this.v$.$invalid) {
        this.reportLoaded = false;
        this.reportGenerating = true;

        this.$axios
          .get('api/v1/reports/tracked-jobs/', {
            params: {
              fromDate: this.fromDate,
              toDate: this.toDate,
            },
          })
          .then((res) => {
            vm.items = Object.values(res.data);
            vm.reportLoaded = true;
          })
          .then(() => {
            vm.reportGenerating = false;
          });
      }
    },
    resetDateValidators() {
      this.v$.fromDate.$reset();
      this.v$.toDate.$reset();
    },
    touchDateValidators() {
      this.v$.fromDate.$touch();
      this.v$.toDate.$touch();
    },
    fromDateOnInputOrBlur() {
      this.reportLoaded = false;
      this.resetDateValidators();
      this.touchDateValidators();

      if (this.fromDate > this.toDate) {
        this.fromDateErrors.push('From Date cannot be after To Date.');
      }
    },
    toDateOnInputOrBlur() {
      this.reportLoaded = false;
      this.resetDateValidators();
      this.touchDateValidators();

      if (this.fromDate > this.toDate) {
        this.toDateErrors.push('To Date cannot be before From Date.');
      }
    },
    getStatusColor(status) {
      return {
        queued: 'info',
        started: 'warning',
        finished: 'success',
        failed: 'error',
      }[status];
    },
    formatDate(date) {
      if (DateTime.fromISO(date).isValid) {
        return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_SHORT);
      }

      if (DateTime.fromSQL(date).isValid) {
        return DateTime.fromSQL(date).toLocaleString(DateTime.DATETIME_SHORT);
      }

      return '';
    },
    closeJsonModal() {
      this.jsonModal = false;
      this.$nextTick(() => {
        this.json = {};
      });
    },
    showJsonModal(title, json) {
      this.json = json;
      this.jsonTitle = title;
      this.jsonModal = true;
    },
  },
  validations: {
    fromDate: { required, lessThanToDate },
    toDate: { required, greaterThanFromDate },
  },
};
</script>

<style></style>
